// consent/consentManager.ts

import { deleteCookie, getCookie, setCookie } from '../utils/cookies';
import { executeCallbacks } from './callbacks';

type ConsentType = 'essential' | 'functional' | 'marketing';

export interface ConsentUpdate {
	consentType: ConsentType;
	consentGiven: boolean;
}

const userConsent: { marketing: boolean; functional: boolean; essential: boolean } = {
	essential: !!getCookie('acceptEssentialCookies'),
	functional: !!getCookie('acceptFunctionalCookies'),
	marketing: !!getCookie('acceptMarketingCookies'),
};

/**
 * Updates user consent statuses in batch and processes callbacks after all updates.
 *
 * @param {ConsentUpdate[]} updates - An array of consent updates to process.
 */
export function updateUserConsent(updates: ConsentUpdate[]): void {
	// Initialize callback storage for granted and revoked consents
	const grantedCallbacks: { [key in ConsentType]: (() => void)[] } = {
		essential: [],
		functional: [],
		marketing: [],
	};

	const revokedCallbacks: { [key in ConsentType]: (() => void)[] } = {
		essential: [],
		functional: [],
		marketing: [],
	};

	// Process each consent update to update cookies and consent state
	updates.forEach(({ consentType, consentGiven }) => {
		const cookieName = `accept${consentType.charAt(0).toUpperCase() + consentType.slice(1)}Cookies`;

		// Update cookie and user consent state
		if (consentGiven) {
			setCookie(cookieName, 'true', 365);
			userConsent[consentType] = true;
		} else {
			deleteCookie(cookieName);
			userConsent[consentType] = false;
		}

		// Collect callbacks based on updated consent state
		if (consentGiven) {
			grantedCallbacks[consentType].push(...executeCallbacks(consentType, 'grant'));
		} else {
			revokedCallbacks[consentType].push(...executeCallbacks(consentType, 'revoke'));
		}
	});

	// Execute all collected callbacks after all updates have been processed
	Object.values(grantedCallbacks)
		.flat()
		.forEach((callback) => callback());
	Object.values(revokedCallbacks)
		.flat()
		.forEach((callback) => callback());
}

/**
 * Retrieves the current consent status for a specific type.
 *
 * @param {ConsentType} consentType - The type of consent to check.
 * @returns {boolean} - The current consent status.
 */
export function getConsentStatus(consentType: ConsentType): boolean {
	return userConsent[consentType];
}

/**
 * Checks if the user has ever set consent for any cookie category.
 *
 * @returns {boolean} - Returns true if the user has set any consent, false otherwise.
 */
export function hasUserSetConsent(): boolean {
	// Check if any of the consent cookies are set
	return (
		!!getCookie('acceptEssentialCookies') ||
		!!getCookie('acceptFunctionalCookies') ||
		!!getCookie('acceptMarketingCookies')
	);
}
